import { Form, Formik, FormikProps } from 'formik'
import React, { useEffect } from 'react'
import { LuChevronDown, LuChevronUp } from 'react-icons/lu'
import * as yup from 'yup'

import DateOfBirthInput from '@/components/form/DateOfBirthInput'
import TextInput from '@/components/form/TextInput'
import CreateNewClientMessage from '@/components/misc/CreateNewClientMessage'
import SelectExistingClientMessage from '@/components/misc/SelectExistingClientMessage'
import SelectClientBase, {
  ClientSelectOption,
} from '@/components/select/SelectClient'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import useDialog from '@/hooks/useDialog'
import { CLIENT_PARTY_TYPE } from '@/lib/constants'
import referralFlowService from '@/lib/services/referralFlowService'
import webReferralService from '@/lib/services/webReferralService'
import { cn } from '@/lib/utils'
import SmallFormGroup from '@/pages/referClient/steps/1_ClientDetails/components/SmallFormGroup'
import { CLIENT_DETAILS_FORM_FIELDS } from '@/pages/referClient/steps/1_ClientDetails/constants'
import { ClientDetailsFormValues } from '@/pages/referClient/steps/1_ClientDetails/types'
import {
  selectLogHistoricalReferralState,
  setIsNewClientFormValid,
  setNewClientData,
  updateLogData,
} from '@/redux/slices/logHistoricalReferral'
import { selectPersistedClient } from '@/redux/slices/webReferral'
interface Props {
  className?: string
  disabled?: boolean
  inlineNameFields?: boolean
  hideOptionalFields?: boolean
}

const ReferralClientDetails: React.FC<Props> = ({
  className,
  disabled = false,
  inlineNameFields = false,
  hideOptionalFields = false,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const referralLogHistoricalState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const dispatch = useAppDispatch()
  const client = useAppSelector(selectPersistedClient)

  const {
    isShowingDialog,
    showDialog: showSelectClient,
    hideDialog: hideSelectClient,
  } = useDialog()

  const isShowingSelectClient =
    isShowingDialog || referralLogHistoricalState.logData.existingClient

  function handleClientSelect(selectedClientOption: ClientSelectOption | null) {
    dispatch(
      updateLogData({
        existingClient: selectedClientOption,
      }),
    )
  }

  return (
    <div className={cn('relative', className)}>
      {isShowingSelectClient ? (
        <>
          <CreateNewClientMessage
            onCreateNew={() => {
              handleClientSelect(null)
              hideSelectClient()
            }}
          />
          <SelectClientBase
            companyId={currentCompany.id}
            selectedClient={referralLogHistoricalState.logData.existingClient}
            onSelect={handleClientSelect}
          />
        </>
      ) : (
        <>
          <SelectExistingClientMessage
            selectTextClassName="mb-3"
            selectedClientName={client?.fullName}
            onOpenSelectionModal={showSelectClient}
            onClearSelection={() => {
              handleClientSelect(null)
              hideSelectClient()
            }}
          />

          <NewClientForm
            disabled={disabled}
            inlineNameFields={inlineNameFields}
            hideOptionalFields={hideOptionalFields}
          />
        </>
      )}
    </div>
  )
}

function NewClientForm({
  disabled = false,
  inlineNameFields = false,
  hideOptionalFields = false,
}: {
  disabled?: boolean
  inlineNameFields?: boolean
  hideOptionalFields?: boolean
}) {
  const formikRef = React.useRef<FormikProps<ClientDetailsFormValues>>(null)
  const validationSchema = yup
    .object()
    .shape(referralFlowService.getClientDetailsValidationSchema())
  const referralLogHistoricalState = useAppSelector(
    selectLogHistoricalReferralState,
  )

  const initialValues = webReferralService.buildInitialClientDetailsValues({
    partyType: CLIENT_PARTY_TYPE.PERSON,
    firstName: referralLogHistoricalState.logData.newClientFirstName,
    lastName: referralLogHistoricalState.logData.newClientLastName,
    email: referralLogHistoricalState.logData.newClientEmail,
  })
  const dispatch = useAppDispatch()

  const [
    showAdditionalClientDetailsSection,
    setShowAdditionalClientDetailsSection,
  ] = React.useState(false)

  const compactFieldGroups = hideOptionalFields // Fine for now - linked to when viewed from interceptor referral logging modal
  const fieldGroupClass = compactFieldGroups ? '!mb-0 !pb-0' : 'mb-3 !pb-0'

  async function handleSubmit(_values: ClientDetailsFormValues) {
    // do nothing
  }

  async function storeFormDataInState(values: ClientDetailsFormValues) {
    // track fields values
    dispatch(setNewClientData(values))
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnMount
      onSubmit={handleSubmit}
      validate={storeFormDataInState}
    >
      {({ isValid }) => (
        <Form>
          <TrackValidity isValid={isValid} />

          <div className={compactFieldGroups ? 'flex flex-col gap-2' : ''}>
            {inlineNameFields ? (
              <div className="flex w-full flex-row gap-3">
                <SmallFormGroup
                  className={cn('min-w-0 flex-1', fieldGroupClass)}
                >
                  <TextInput
                    isDisabled={disabled}
                    label="First name *"
                    name={CLIENT_DETAILS_FORM_FIELDS.firstName}
                    placeholder="First name *"
                  />
                </SmallFormGroup>

                <SmallFormGroup
                  className={cn('min-w-0 flex-1', fieldGroupClass)}
                >
                  <TextInput
                    isDisabled={disabled}
                    label="Last name *"
                    name={CLIENT_DETAILS_FORM_FIELDS.lastName}
                    placeholder="Last name *"
                  />
                </SmallFormGroup>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 w-full gap-3">
                <SmallFormGroup className={fieldGroupClass}>
                  <TextInput
                    isDisabled={disabled}
                    name={CLIENT_DETAILS_FORM_FIELDS.firstName}
                    placeholder="First name *"
                    autoFocus
                  />
                </SmallFormGroup>

                <SmallFormGroup className={fieldGroupClass}>
                  <TextInput
                    isDisabled={disabled}
                    name={CLIENT_DETAILS_FORM_FIELDS.lastName}
                    placeholder="Last name *"
                  />
                </SmallFormGroup>
              </div>
            )}

            <SmallFormGroup className={fieldGroupClass}>
              <TextInput
                isDisabled={disabled}
                name={CLIENT_DETAILS_FORM_FIELDS.email}
                label={inlineNameFields && 'Email *'}
                placeholder="Email *"
              />
            </SmallFormGroup>

            {!hideOptionalFields && (
              <>
                <div
                  className={
                    showAdditionalClientDetailsSection
                      ? 'flex flex-col gap-2'
                      : 'hidden'
                  }
                >
                  <AdditionalNewClientFields disabled={disabled} />
                </div>
                <div
                  className="hover-dim mb-1 inline-flex cursor-pointer
                    items-center"
                  onClick={() =>
                    setShowAdditionalClientDetailsSection(
                      !showAdditionalClientDetailsSection,
                    )
                  }
                >
                  Optional fields
                  {showAdditionalClientDetailsSection ? (
                    <LuChevronUp className="size-4" />
                  ) : (
                    <LuChevronDown className="size-4" />
                  )}
                </div>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

function AdditionalNewClientFields({
  disabled = false,
}: {
  disabled?: boolean
}) {
  return (
    <>
      <SmallFormGroup className="mb-0">
        <TextInput
          isDisabled={disabled}
          name={CLIENT_DETAILS_FORM_FIELDS.telephone}
          placeholder="Telephone"
        />
      </SmallFormGroup>

      <SmallFormGroup>
        <DateOfBirthInput
          label="Date of birth"
          name={CLIENT_DETAILS_FORM_FIELDS.dateOfBirth}
          isDisabled={disabled}
        />
      </SmallFormGroup>
    </>
  )
}

function TrackValidity({ isValid }: { isValid: boolean }) {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setIsNewClientFormValid(isValid))
  }, [dispatch, isValid])

  return null
}

export default ReferralClientDetails
